import { useState } from 'react';
import { X, User, Phone, CheckCircle } from 'lucide-react';

interface CallbackRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseTitle?: string;
}

const WEB3FORMS_ACCESS_KEY = '5bf31f55-ad2e-42d0-b75d-da315233314c';

const CallbackRequestModal = ({ isOpen, onClose, courseTitle }: CallbackRequestModalProps) => {
  const [prenom, setPrenom] = useState('');
  const [telephone, setTelephone] = useState('');
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    onClose();
    // Laisse la transition de fermeture se jouer avant de réinitialiser l'état
    setTimeout(() => {
      setPrenom('');
      setTelephone('');
      setSubmitted(false);
      setError(false);
    }, 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prenom || !telephone) return;

    setSending(true);
    setError(false);

    const formData = new FormData();
    formData.append('access_key', WEB3FORMS_ACCESS_KEY);
    formData.append('subject', `[Demande de rappel] ${prenom} — ${courseTitle ?? 'Formation IA'}`);
    formData.append('name', prenom);
    formData.append('phone', telephone);
    formData.append(
      'message',
      `Demande de rappel\n\nPrénom : ${prenom}\nTéléphone : ${telephone}\nFormation concernée : ${courseTitle ?? 'Non précisée'}`
    );

    try {
      const response = await fetch('https://api.web3forms.com/submit', { method: 'POST', body: formData });
      const res = await response.json();
      if (res.success) {
        setSubmitted(true);
        if (typeof (window as any).gtag_report_conversion === 'function') {
          (window as any).gtag_report_conversion();
        }
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 md:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          aria-label="Fermer"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {submitted ? (
          <div className="text-center py-6">
            <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-7 w-7 text-green-600" />
            </div>
            <p className="text-lg font-semibold text-gray-900 mb-2">Merci !</p>
            <p className="text-gray-600">Un membre de notre équipe vous rappelle très prochainement.</p>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Être rappelé</h3>
            <p className="text-sm text-gray-500 mb-6">
              Laissez-nous votre prénom et votre numéro, nous vous rappelons rapidement pour parler de votre projet
              {courseTitle ? ` autour de « ${courseTitle} »` : ''}.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Prénom *</label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    required
                    value={prenom}
                    onChange={(e) => setPrenom(e.target.value)}
                    placeholder="Votre prénom"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Téléphone *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    required
                    value={telephone}
                    onChange={(e) => setTelephone(e.target.value)}
                    placeholder="06 12 34 56 78"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none"
                  />
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-600">Une erreur est survenue, merci de réessayer.</p>
              )}

              <button
                type="submit"
                disabled={sending}
                className="w-full btn-primary justify-center disabled:opacity-60"
              >
                {sending ? 'Envoi...' : 'Envoyer ma demande'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default CallbackRequestModal;
